import { getChainContracts } from "@/shared/config/blockchain/helpers";
import { useWeb3State } from "@/web3/context";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import useERC20Contract from "../contracts/ERC20/useERC20Contract";
import useMutationWithHandler from "../hooks/useMutationWithHandler";
import { MutationArgs } from "../types";
import useERC20AllowanceQuery from "./useERC20AllowanceQuery";

const useERC20ApprovalMutation = (
  tokenAddress?: string,
  operator?: string,
  mutationArgs: MutationArgs = {},
) => {
  const { chainId, currentAddress } = useWeb3State();

  const contracts = getChainContracts(chainId);

  const erc20Contract = useERC20Contract(tokenAddress);

  const queryClient = useQueryClient();

  const { allowance, isAllowanceLoading } = useERC20AllowanceQuery(
    currentAddress,
    operator,
    tokenAddress,
  );

  const { mutateAsync: approve, isLoading: isApproveLoading } =
    useMutationWithHandler(
      async (amount: string) => {
        if (!erc20Contract || !contracts) {
          return;
        }

        const tx = await erc20Contract.approve(operator, amount);

        return await tx.wait();
      },
      {
        ...mutationArgs,
        onSuccess: (...arg) => {
          queryClient.invalidateQueries([
            "erc20-allowance",
            tokenAddress,
            erc20Contract,
            chainId,
            currentAddress,
          ]);

          mutationArgs.onSuccess?.(...arg);
        },
      },
    );

  const approveWithCheck = useCallback(
    async (requiredAmount: bigint) => {
      if (!allowance || requiredAmount > allowance) {
        const isApproveSuccess = await approve(requiredAmount);

        if (!isApproveSuccess) {
          return false;
        }
      }
      return true;
    },
    [allowance, approve],
  );

  return {
    approve: approveWithCheck,
    isLoading: isApproveLoading || isAllowanceLoading,
  };
};

export default useERC20ApprovalMutation;
